import Button from 'devextreme-react/button';
import TextBox from 'devextreme-react/text-box';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import {
  createBlankStarterInUserStorage,
  type MyAppsWorkspaceStarterId
} from '../fileBrowser/createBlankStarterInUserStorage';
import { USER_STORAGE_MOUNT_ID } from '../storageFileAudit/formatters';
import { showErrorToast } from '../notifications/toast';
import {
  CODE_EDITOR_TEMPLATES,
  defaultCodeEditorFileName,
  validateCodeEditorFileName,
  type CodeEditorTemplate
} from './codeEditorTemplates';
import './codeEditorCreate.css';

export type CodeEditorCreateResult = {
  mountId: string;
  relativePath: string;
  fileName: string;
  starterId: MyAppsWorkspaceStarterId;
};

export type CodeEditorCreatePanelProps = {
  /** Optional intro line above the template grid. */
  intro?: ReactNode;
  /** Extra actions beside Create (e.g. link back to Files). */
  secondaryAction?: ReactNode;
  mountId?: string;
  onCreated: (result: CodeEditorCreateResult) => void;
};

export function CodeEditorCreatePanel({
  intro = 'Pick a language or config template, name the file, then open it in the Code Editor.',
  secondaryAction,
  mountId = USER_STORAGE_MOUNT_ID,
  onCreated
}: CodeEditorCreatePanelProps) {
  const [selectedId, setSelectedId] = useState<MyAppsWorkspaceStarterId>('json');
  const [fileName, setFileName] = useState(() => defaultCodeEditorFileName('json'));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const selected = useMemo(
    () => CODE_EDITOR_TEMPLATES.find((t) => t.starterId === selectedId) ?? CODE_EDITOR_TEMPLATES[0],
    [selectedId]
  );

  const selectTemplate = useCallback((template: CodeEditorTemplate) => {
    setSelectedId(template.starterId);
    setFileName(defaultCodeEditorFileName(template.starterId));
    setError(null);
  }, []);

  const createAndOpen = useCallback(async () => {
    const validationError = validateCodeEditorFileName(fileName, selected.extension);
    if (validationError) {
      setError(validationError);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const created = await createBlankStarterInUserStorage(
        selected.starterId,
        mountId,
        fileName.trim()
      );
      onCreated({
        mountId: created.mountId,
        relativePath: created.relativePath,
        fileName: created.fileName,
        starterId: selected.starterId
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not create code file.';
      setError(message);
      showErrorToast(message);
      setBusy(false);
    }
  }, [fileName, mountId, onCreated, selected.extension, selected.starterId]);

  return (
    <div className="code-editor-create">
      {intro ? <p className="code-editor-create__intro">{intro}</p> : null}

      <section className="code-editor-create__templates" aria-label="File templates">
        {CODE_EDITOR_TEMPLATES.map((template) => {
          const active = template.starterId === selected.starterId;
          return (
            <button
              key={template.starterId}
              type="button"
              className={
                active
                  ? 'code-editor-create__card code-editor-create__card--active'
                  : 'code-editor-create__card'
              }
              aria-pressed={active}
              disabled={busy}
              onClick={() => selectTemplate(template)}
            >
              <span className="code-editor-create__card-title">{template.title}</span>
              <span className="code-editor-create__card-ext">{template.extension}</span>
              <span className="code-editor-create__card-desc">{template.description}</span>
            </button>
          );
        })}
      </section>

      <section className="code-editor-create__form" aria-label="Create file">
        <label className="code-editor-create__label" htmlFor="code-editor-create-filename">
          File name
        </label>
        <TextBox
          value={fileName}
          disabled={busy}
          stylingMode="outlined"
          inputAttr={{ id: 'code-editor-create-filename' }}
          onValueChanged={(e) => setFileName(String(e.value ?? ''))}
          onEnterKey={() => void createAndOpen()}
        />
        <p className="code-editor-create__hint">
          Must use the {selected.extension} extension for {selected.title}.
        </p>
        {error ? (
          <p className="code-editor-create__error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="code-editor-create__actions">
          <Button
            text={busy ? 'Creating…' : 'Create and open'}
            type="default"
            disabled={busy}
            onClick={() => void createAndOpen()}
          />
          {secondaryAction}
        </div>
      </section>
    </div>
  );
}
